import React, { useMemo, useState, useEffect } from 'react';
import { Container, Typography, Box, Chip, Avatar, Divider, Grid, Paper, IconButton, Button, Stack, TextField } from '@mui/material';
import { ArrowBackIosNew, ShareOutlined, BookmarkBorder, FavoriteBorder, CalendarToday, AccessTime, Label } from '@mui/icons-material';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { posts } from '../components/Blog';

function estimateReadTime(text) {
  if (!text) return '1 min read';
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

function CommentsSection({ slug }) {
  const storageKey = `blog_comments_${slug}`;
  const [comments, setComments] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });
  const [input, setInput] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(comments));
    } catch (e) {}
  }, [comments, storageKey]);

  function addComment() {
    if (!input.trim()) return;
    const c = { id: Date.now(), text: input.trim(), when: new Date().toISOString() };
    setComments(prev => [c, ...prev]);
    setInput('');
  }

  return (
    <Box>
      <Stack spacing={2}>
        <TextField value={input} onChange={(e) => setInput(e.target.value)} label="Write a comment" multiline minRows={2} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={addComment}>Post comment</Button>
          <Button variant="outlined" onClick={() => setInput('')}>Clear</Button>
        </Box>

        {comments.length === 0 ? (
          <Typography color="text.secondary">Be the first to comment.</Typography>
        ) : (
          comments.map(c => (
            <Paper key={c.id} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2">{c.text}</Typography>
              <Typography variant="caption" color="text.secondary">{new Date(c.when).toLocaleString()}</Typography>
            </Paper>
          ))
        )}
      </Stack>
    </Box>
  );
}

export default function BlogPostViewClean() {
  const { slug } = useParams();
  const post = useMemo(() => posts.find(p => p.slug === slug), [slug]);

  if (!post) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography variant="h4" gutterBottom>Post not found</Typography>
        <Typography color="text.secondary">We couldn't find that article. Try returning to the blog list.</Typography>
        <Button component={RouterLink} to="/blog" sx={{ mt: 2 }} startIcon={<ArrowBackIosNew />}>Back to Blog</Button>
      </Container>
    );
  }

  const readTime = post.readTime || estimateReadTime(post.content || post.excerpt);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
        <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Box component="img" src={post.image} alt={post.title} sx={{ width: '100%', height: { xs: 240, md: 420 }, objectFit: 'cover', display: 'block' }} />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.5) 70%)' }} />
            <Box sx={{ position: 'absolute', bottom: 16, left: 24, color: 'common.white' }}>
              <Typography variant="overline" sx={{ display: 'block', opacity: 0.95 }}>{post.category}</Typography>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>{post.title}</Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1, alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ width: 36, height: 36 }}>{post.author ? post.author.charAt(0) : 'N'}</Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>{post.author}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>{post.date} • {readTime}</Typography>
                  </Box>
                </Stack>
                <Box sx={{ ml: 2 }}>
                  <Chip label={post.category} color="secondary" />
                </Box>
              </Stack>
            </Box>
            <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
              <Button component={RouterLink} to="/blog" variant="contained" color="inherit" startIcon={<ArrowBackIosNew />} size="small">Back</Button>
            </Box>
            <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
              <Stack direction="row" spacing={1}>
                <IconButton aria-label="share" color="primary" onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: post.title, url: window.location.href }).catch(()=>{});
                  } else {
                    navigator.clipboard?.writeText(window.location.href);
                  }
                }}>
                  <ShareOutlined />
                </IconButton>
                <IconButton aria-label="bookmark"><BookmarkBorder /></IconButton>
                <IconButton aria-label="like"><FavoriteBorder /></IconButton>
              </Stack>
            </Box>
          </Box>

          <Grid container>
            <Grid item xs={12} md={8}>
              <Box sx={{ p: { xs: 3, md: 6 } }}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>{post.excerpt}</Typography>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ typography: 'body1', color: 'text.primary' }}>
                  {post.content ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
                  ) : (
                    (post.excerpt || '').split('\n\n').map((para, idx) => (
                      <Typography key={idx} paragraph dangerouslySetInnerHTML={{ __html: para.replace(/\n/g, '<br/>') }} />
                    ))
                  )}
                </Box>

                <Box sx={{ mt: 4, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Label sx={{ mr: 1 }} />
                  {(post.tags || []).map((t) => (<Chip key={t} label={t} size="small" variant="outlined" />))}
                </Box>

                <Divider sx={{ my: 4 }} />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>Comments</Typography>
                  <CommentsSection slug={post.slug} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ p: { xs: 3, md: 4 }, borderLeft: { md: '1px solid rgba(0,0,0,0.06)' } }}>
                <Typography variant="subtitle2" gutterBottom>About the author</Typography>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56 }}>{post.author ? post.author.charAt(0) : 'N'}</Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{post.author}</Typography>
                    <Typography variant="caption" color="text.secondary">Author • NehaInfoTech</Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <CalendarToday fontSize="small" />
                  <Typography variant="caption" color="text.secondary">{post.date}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <AccessTime fontSize="small" />
                  <Typography variant="caption" color="text.secondary">{readTime}</Typography>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>Related posts</Typography>
                <Stack spacing={1}>
                  {posts.filter(p => p.slug !== post.slug).slice(0,4).map((p) => (
                    <Button key={p.slug} component={RouterLink} to={`/blog/${p.slug}`} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                      <Typography variant="body2">{p.title}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{p.readTime || ''}</Typography>
                    </Button>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>
    </Container>
  );
}
